<template>
  <q-dialog
    :model-value="true"
    transition-show="fade-in"
    transition-hide="fade-out"
    data-test-id="preview-permissions-dialog"
    @hide="$emit('hide')"
  >
    <q-card class="dialog-body">
      <q-card-section class="q-pa-sm">
        <div class="row items-center no-wrap">
          <div class="col">
            <q-btn
              v-close-popup
              icon="close"
              flat
              round
              dense
            />
          </div>
          <div class="col-auto text-h6">
            Preview Permissions
          </div>
          <div class="col text-right">
            <q-btn
              v-if="currentPreviewedPermissionActions.length"
              label="Stop"
              color="negative"
              outline
              @click="stopPreviewingPermissions"
            >
              <q-tooltip>
                Stop previewing permissions and return to your own permissions
              </q-tooltip>
            </q-btn>
            <q-btn
              class="q-ml-sm"
              label="Start Preview"
              color="primary"
              :outline="!hasUnsavedChanges || !newPermissionActionsToPreview.length"
              :disable="!hasUnsavedChanges || !newPermissionActionsToPreview.length"
              @click="updatePreviewedPermissions"
            >
              <q-tooltip>
                Temporarily replace your own permissions with the selected permissions
              </q-tooltip>
            </q-btn>
          </div>
        </div>
      </q-card-section>

      <q-separator />

      <q-card-section
        class="items-stretch"
        :horizontal="$q.screen.gt.xs"
      >
        <q-card-section class="col-6 column q-pa-none no-wrap options-section">
          <q-tabs
            v-model="tab"
            class="col-auto"
          >
            <q-tab
              :name="TABS.roles"
              label="Roles"
            />
            <q-tab
              :name="TABS.permissions"
              label="Permissions"
            />
            <q-tab
              :name="TABS.users"
              label="Users"
            />
            <q-tab
              :name="TABS.yours"
              label="Yours"
            />
          </q-tabs>
          <q-tab-panels
            v-if="$isPreviewPermissionsPermitted()"
            v-model="tab"
            class="col"
          >
            <q-tab-panel
              class="q-pt-none"
              :name="TABS.roles"
            >
              <roles-tab-menu
                :previewed-permission-actions="newPermissionActionsToPreview"
                @select-permission-actions="handleSelectedPermissionActions"
                @unselect-permission-actions="handleUnselectedPermissionActions"
              />
            </q-tab-panel>
            <q-tab-panel
              class="q-pt-none"
              :name="TABS.permissions"
            >
              <permissions-tab-menu
                :previewed-permission-actions="newPermissionActionsToPreview"
                @select-permission-actions="handleSelectedPermissionActions"
                @unselect-permission-actions="handleUnselectedPermissionActions"
              />
            </q-tab-panel>
            <q-tab-panel
              class="q-pt-none"
              :name="TABS.users"
            >
              <users-tab-menu
                :previewed-permission-actions="newPermissionActionsToPreview"
                @select-permission-actions="handleSelectedPermissionActions"
                @unselect-permission-actions="handleUnselectedPermissionActions"
              />
            </q-tab-panel>
            <q-tab-panel
              class="q-pt-none"
              :name="TABS.yours"
            >
              <yours-tab-menu
                :previewed-permission-actions="newPermissionActionsToPreview"
                @select-permission-actions="handleSelectedPermissionActions"
                @unselect-permission-actions="handleUnselectedPermissionActions"
              />
            </q-tab-panel>
          </q-tab-panels>
        </q-card-section>

        <q-separator :vertical="$q.screen.gt.xs" />

        <q-card-section class="col q-py-none options-section">
          <selected-permissions-list
            v-if="$isPreviewPermissionsPermitted()"
            class="full-height"
            :selected-permission-actions="newPermissionActionsToPreview"
            :unsaved-change-count="unsavedChangeCount"
            @unselect-permission-actions="handleUnselectedPermissionActions"
            @preview-empty-permissions="previewEmptyPermissions"
            @clear="clear"
            @revert="revert"
          />
        </q-card-section>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import usePreviewPermissions from 'src/composables/usePreviewPermissions'
import PermissionsTabMenu from 'src/modules/admin/dialogs/PreviewPermissionsDialog/components/PermissionsTabMenu'
import RolesTabMenu from 'src/modules/admin/dialogs/PreviewPermissionsDialog/components/RolesTabMenu'
import SelectedPermissionsList from 'src/modules/admin/dialogs/PreviewPermissionsDialog/components/SelectedPermissionsList'
import UsersTabMenu from 'src/modules/admin/dialogs/PreviewPermissionsDialog/components/UsersTabMenu'
import YoursTabMenu from 'src/modules/admin/dialogs/PreviewPermissionsDialog/components/YoursTabMenu'
import { resetMenuList } from 'src/modules/admin/search/MenuSearchComponent'

const TABS = {
  roles: 'roles',
  permissions: 'permissions',
  users: 'users',
  yours: 'yours'
}

export default {
  name: 'PreviewPermissionsDialog',
  components: {
    PermissionsTabMenu,
    RolesTabMenu,
    SelectedPermissionsList,
    UsersTabMenu,
    YoursTabMenu
  },
  emits: ['hide'],
  setup () {
    const {
      previewedPermissions: currentPreviewedPermissionActions,
      previewPermissions,
      previewEmptyPermissions,
      stopPreviewingPermissions
    } = usePreviewPermissions()

    return {
      currentPreviewedPermissionActions,
      previewPermissions,
      previewEmptyPermissions,
      stopPreviewingPermissions
    }
  },
  data () {
    return {
      tab: TABS.roles,
      newPermissionActionsToPreview: []
    }
  },
  computed: {
    hasUnsavedChanges () {
      return this.unsavedChangeCount > 0
    },
    unsavedChangeCount () {
      return _.difference(this.newPermissionActionsToPreview, this.currentPreviewedPermissionActions).length +
        _.difference(this.currentPreviewedPermissionActions, this.newPermissionActionsToPreview).length
    }
  },
  watch: {
    currentPreviewedPermissionActions: {
      handler () {
        this.newPermissionActionsToPreview = this.currentPreviewedPermissionActions
        resetMenuList()
      },
      immediate: true
    }
  },
  created () {
    _.extend(this, { TABS })
  },
  methods: {
    handleSelectedPermissionActions (...permissionActions) {
      permissionActions = _.flattenDeep([permissionActions])
      this.newPermissionActionsToPreview = _.uniq([...this.newPermissionActionsToPreview, ...permissionActions])
    },
    handleUnselectedPermissionActions (...permissionActions) {
      permissionActions = _.flattenDeep([permissionActions])
      this.newPermissionActionsToPreview = _.difference(this.newPermissionActionsToPreview, permissionActions)
    },
    updatePreviewedPermissions () {
      this.previewPermissions(this.newPermissionActionsToPreview)
    },
    clear () {
      this.newPermissionActionsToPreview = []
    },
    revert () {
      this.newPermissionActionsToPreview = this.currentPreviewedPermissionActions
    }
  }
}
</script>

<style scoped>
.dialog-body {
  width: 900px;
  max-width: 98%;
}
.options-section {
  min-height: 600px;
  max-height: 70vh;
  overflow: auto;
}
.selected-permissions {
  height: 100%;
  min-height: 500px;
  max-height: 70vh;
}

@media (max-width: 600px) {
  .options-section,
  .selected-permissions {
    min-height: 0;
    max-height: 90vh;
  }
}

.arrow {
  font-size: 40px;
}
</style>
